import { err, ok, type Result } from "neverthrow";
import type { Opaque, SnakeCase } from "type-fest";
import { type AnyObjectSchema, type InferType, ValidationError } from "yup";
import type { FormData, SR as SRScript, SrClientUpdatePayload } from "./types";

declare const SR: SRScript;

type ValidFormToSendSynerise<Type extends string, Data extends AnyObjectSchema["__outputType"]> = Opaque<
  { formType: SnakeCase<Type>; formData: Data },
  "Form"
>;
type FormResult<Type extends string, Data extends AnyObjectSchema["__outputType"]> = Result<
  ValidFormToSendSynerise<Type, Data>,
  ValidationError | Error
>;

const getSR = () => {
  return SR;
};

export const formTypeFactory =
  <Type extends string, Schema extends AnyObjectSchema>(formType: SnakeCase<Type>, schema: Schema) =>
  (formData: InferType<Schema>) => {
    try {
      const validData = schema.validateSync(formData, { stripUnknown: true });
      return ok({ formType, formData: validData } as ValidFormToSendSynerise<Type, typeof validData>);
    } catch (error) {
      return err(ValidationError.isError(error) ? error : new Error(`Unexpected error: ${String(error)}`));
    }
  };

export const sendSyneriseForm = <Type extends string, Data extends FormData>(
  form: FormResult<Type, Data>,
): Promise<void> => {
  const sr = getSR();

  if (!sr) return Promise.resolve();

  if (form.isErr()) {
    console.error(form.error);
    return Promise.resolve();
  }

  const { formType, formData } = form.value;

  // Normalizujemy wynik SDK do Promise, która NIGDY nie rzuca ani nie odrzuca — dzięki temu
  // wołający mogą bezpiecznie dopiąć `.then()` (np. SR.client.update z tagami origin/nlsource),
  // a błąd wysyłki formularza (synchroniczny rzut lub odrzucona Promise) nie przerwie flow.
  try {
    return Promise.resolve(sr.event.sendFormData(formType, formData)).then(
      () => undefined,
      (error: unknown) => {
        console.error(error);
      },
    );
  } catch (error) {
    console.error(error);
    return Promise.resolve();
  }
};

export const updateSyneriseClient = (payload: SrClientUpdatePayload) => {
  const sr = getSR();

  // No-op gdy SDK niezaładowane (brak zgody analitycznej c4) lub gdy brak metody client.update.
  if (typeof sr?.client?.update !== "function") return;

  // Błąd update'u nie może przerwać flow po sendFormData (spójnie z resilience sendSyneriseForm).
  try {
    sr.client.update(payload);
  } catch (error) {
    console.error(error);
  }
};
